import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, Play, Shuffle, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api, CscaTopicAnalysisResponse } from '../utils/api';

const CSCAMoocRandom: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CscaTopicAnalysisResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.getCscaMoocTopics(token, 3)
      .then(data => {
        if (cancelled) return;
        setCatalog(data);
      })
      .catch((requestError: Error) => {
        if (!cancelled) setError(requestError.message || '无法加载 MOOC 题库');
      });
    return () => { cancelled = true; };
  }, [token]);

  const start = () => {
    if ((catalog?.complete_set_count || 0) < 5) return;
    navigate('/csca/mooc/random/exam');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-10">
      <Link to="/csca" className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5] hover:text-indigo-700"><ArrowLeft className="h-4 w-4" /> 返回 CSCA 数学</Link>

      <section className="rounded-3xl bg-gradient-to-br from-cyan-700 via-teal-600 to-emerald-600 p-6 text-white shadow-xl shadow-cyan-500/20 md:p-9">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-cyan-100"><Sparkles className="h-5 w-5" /> CSCA 数学 · MOOC</div>
            <h1 className="mt-3 text-3xl font-black md:text-4xl">随机 MOOC</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-cyan-50 md:text-base">5 套数学模拟题已加入 MOOC 题库。每个题号优先抽取你尚未做过的模拟题；该题号的模拟题做完后，再抽取 AI 题。每次固定 48 题。</p>
          </div>
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-white/15 backdrop-blur"><Shuffle className="h-10 w-10" /></div>
        </div>
      </section>

      {error && <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /><span>{error}</span></div>}

      <section className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-3xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8">
          <h2 className="text-2xl font-black text-[#312E81]">随机生成完整模拟卷</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">固定48题、60分钟。每个位置只抽1题，位置01到48保持不变；刷新后会恢复同一场未完成考试。</p>
          <div className="mt-6 rounded-2xl border border-cyan-100 bg-cyan-50 p-5">
            <div className="text-sm font-bold text-cyan-700">组卷来源</div>
            <div className="mt-2 text-3xl font-black text-[#312E81]">{catalog?.complete_set_count ?? '—'} 套 × 48题</div>
            <div className="mt-1 text-sm text-slate-500">模拟题 {catalog?.simulation_question_count ?? '—'} 道 · AI 题 {catalog?.ai_question_count ?? '—'} 道</div>
          </div>
          <button type="button" onClick={start} disabled={(catalog?.complete_set_count || 0) < 5} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-teal-600 px-5 py-4 text-lg font-black text-white shadow-lg shadow-teal-500/20 hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"><Play className="h-5 w-5 fill-current" /> 随机生成48题并开始</button>
        </div>

        <div className="rounded-3xl border border-indigo-100 bg-indigo-50/60 p-6 md:p-8">
          <div className="text-sm font-bold text-indigo-500">当前题库</div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white p-4 text-center"><div className="text-3xl font-black text-[#312E81]">{catalog?.complete_set_question_count ?? '—'}</div><div className="mt-1 text-xs text-slate-500">完整套卷题</div></div>
            <div className="rounded-2xl bg-white p-4 text-center"><div className="text-3xl font-black text-[#312E81]">{catalog?.topics.length ?? '—'}</div><div className="mt-1 text-xs text-slate-500">粗分类</div></div>
          </div>
          <p className="mt-4 text-sm leading-6 text-slate-600">每个位置保留原题号。优先使用未做过的模拟题，再使用未做过的 AI 题；已做过的题会在可用新题用完后出现。</p>
        </div>
      </section>
    </div>
  );
};

export default CSCAMoocRandom;
